/** Константы и метаданные лендинга мобильного приложения на портале. */

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "https://api.uchetkm.ru"

export const MOBILE_APP = {
  name: "Учёт КМ",
  productName: "Мобильное приложение «Учёт КМ»",
  path: "/mobile",
  profileAppsPath: "/profile/apps",
  fallbackVersion: "1.0",
  fallbackVersionDate: "06.10.2026",
  images: {
    home: "/services/mobile/home.jpg",
    settingsQr: "/services/mobile/settings-qr.jpg",
    checkStructure: "/services/mobile/check-structure.jpg",
    checkCard: "/services/mobile/check-card.jpg",
    packsList: "/services/mobile/packs-list.jpg",
    packsScan: "/services/mobile/packs-scan.jpg",
    inventoryCreate: "/services/mobile/inventory-create.jpg",
    inventoryScan: "/services/mobile/inventory-scan.jpg",
    copyPreview: "/services/mobile/copy-preview.jpg",
    tsdEmulator: "/services/mobile/tsd-emulator.jpg",
    settings: "/services/mobile/settings.jpg",
  },
} as const

export type MobileAppStoreKey = "rustore" | "google_play" | "apple"

export type MobileAppLandingInfo = {
  version: string
  version_date: string
  download_count: number
  download_filename: string
  has_apk: boolean
  apk_url: string
  rustore_url: string | null
  google_play_url: string | null
  apple_store_url: string | null
}

export function defaultMobileAppLanding(): MobileAppLandingInfo {
  const version = MOBILE_APP.fallbackVersion
  return {
    version,
    version_date: MOBILE_APP.fallbackVersionDate,
    download_count: 0,
    download_filename: `markirovka-mobile_${version}.apk`,
    has_apk: false,
    apk_url: `${API_BASE}/api/v1/public/mobile-app/apk`,
    rustore_url: null,
    google_play_url: null,
    apple_store_url: null,
  }
}

function nullableUrl(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed !== "" ? trimmed : null
}

export async function fetchMobileAppLanding(): Promise<MobileAppLandingInfo> {
  const fallback = defaultMobileAppLanding()
  try {
    const res = await fetch(`${API_BASE}/api/v1/public/mobile-app/landing`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
    if (!res.ok) return fallback
    const data = (await res.json()) as Partial<MobileAppLandingInfo>
    const version =
      typeof data.version === "string" && data.version.trim() !== ""
        ? data.version.trim()
        : fallback.version
    return {
      version,
      version_date:
        typeof data.version_date === "string" && data.version_date.trim() !== ""
          ? data.version_date.trim()
          : fallback.version_date,
      download_count:
        typeof data.download_count === "number"
          ? data.download_count
          : fallback.download_count,
      download_filename:
        typeof data.download_filename === "string" &&
        data.download_filename.trim() !== ""
          ? data.download_filename.trim()
          : `markirovka-mobile_${version}.apk`,
      has_apk: Boolean(data.has_apk),
      apk_url:
        typeof data.apk_url === "string" && data.apk_url.trim() !== ""
          ? data.apk_url.trim()
          : fallback.apk_url,
      rustore_url: nullableUrl(data.rustore_url),
      google_play_url: nullableUrl(data.google_play_url),
      apple_store_url: nullableUrl(data.apple_store_url),
    }
  } catch {
    return fallback
  }
}

export async function trackMobileAppStoreClick(
  store: MobileAppStoreKey,
): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/public/mobile-app/store-clicks`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ store }),
      cache: "no-store",
    })
    if (!res.ok) return null
    const data = (await res.json()) as { url?: string }
    return typeof data.url === "string" ? data.url : null
  } catch {
    return null
  }
}

export const MOBILE_APP_FAQ = [
  {
    q: "Можно ли работать на складе без интернета?",
    a: "Да. Проверка структуры кодов, сбор товаров в пакеты и инвентаризация по заранее загруженному файлу работают полностью автономно. Готовые файлы Excel сохраняются в телефоне, и вы сможете отправить их, когда появится связь.",
  },
  {
    q: "Можно ли печатать этикетки без компьютера?",
    a: "Да. Подключите термопринтер этикеток к смартфону через USB OTG-переходник и разрешите доступ к устройству. Печать на 58×40 мм доступна бесплатно без токена, с токеном открываются все размеры от 15×15 до 58×60 мм. Поддерживаются протоколы TSPL, ESC/POS и ZPL.",
  },
  {
    q: "На каком расстоянии от компьютера работает режим беспроводного сканера?",
    a: "На любом расстоянии. Связь идёт через интернет (Wi-Fi или мобильная сеть). Сотрудник может сканировать на складе или на рампе, а коды появляются в 1С на компьютере в офисе.",
  },
  {
    q: "Почему Excel открывает коды маркировки без ошибок?",
    a: "Файлы отчётов формируются так, чтобы длинные последовательности цифр сохранялись как текст: номера не превращаются в формулы, а разделители маркировки не искажаются.",
  },
  {
    q: "Что делать, если в режиме эмулятора ТСД статус показывает «Ожидание»?",
    a: "Убедитесь, что на компьютере открыта программа DMCopy Pro, в ней включён режим приёма кодов от ТСД, а компьютер подключён к интернету. Обычно связь устанавливается за несколько секунд.",
  },
] as const
